/**
 * Helper utilities for hey-api SDK functions
 */

import type { AxiosResponse } from 'axios';
import { filter, map, type OperatorFunction } from 'rxjs';

/**
 * Creates options for hey-api SDK calls that throw errors only for specific HTTP status codes.
 * By default, Axios throws on all non-2xx status codes. This helper allows selective error throwing.
 *
 * @param statusCodes - Array of HTTP status codes that should throw errors
 * @returns Options object to pass to hey-api SDK functions
 *
 * @example
 * const { data } = await whoAmIApi(throwOnStatusCodes([403]));
 */
export const throwOnStatusCodes = (statusCodes: number[]) => ({
	validateStatus: (status: number) => !statusCodes.includes(status),
});

/**
 * Creates options for hey-api SDK calls that DON'T throw errors for specific HTTP status codes.
 * Useful when you want to handle certain error codes gracefully without try-catch.
 *
 * @param statusCodes - Array of HTTP status codes that should NOT throw errors
 * @returns Options object to pass to hey-api SDK functions
 *
 * @example
 * const { data, error } = await getUserApi({ id: 123 }, ignoreStatusCodes([404]));
 */
export const ignoreStatusCodes = (statusCodes: number[]) => ({
	validateStatus: (status: number) => {
		// Accept all 2xx status codes OR the specified codes to ignore
		return (status >= 200 && status < 300) || statusCodes.includes(status);
	},
});

/**
 * Type guard for narrowing hey-api SDK response types based on HTTP status code.
 *
 * Hey-API SDK returns response data as a union of all possible status code types,
 * but TypeScript cannot automatically narrow the type based on the status code.
 * This type guard enables proper type narrowing.
 *
 * @param response - The response object from a hey-api SDK call
 * @param status - The specific HTTP status code to check for
 * @returns Type predicate that narrows response.data to the specific status type
 *
 * @example
 * const res = await getDriverLocationApi({ path: { uuid: legId } });
 * if (isStatus<GetDriverLocationResponses, 200>(res, 200)) {
 *   console.log(res.data.licensePlate); // ✅ Type-safe access
 * }
 */
export function isStatus<
	TResponses extends Record<number, any>,
	TStatus extends keyof TResponses
>(
	response: AxiosResponse<TResponses[keyof TResponses]>,
	status: TStatus
): response is AxiosResponse<TResponses[TStatus]> & { status: TStatus } {
	return response.status === status;
}

/**
 * Extracts data from a successful (2xx) hey-api SDK response or returns null for other status codes.
 *
 * This is a convenience wrapper around `isStatus` for the common pattern of handling
 * successful responses vs returning null for non-success cases.
 *
 * @param successStatus - The HTTP status code considered successful (typically 200 or 201)
 * @returns Function that extracts response data or returns null
 *
 * @example
 * getDriverLocationApi({ path: { uuid: legId } }).then(
 *   extractData<GetDriverLocationResponses, 200>(200)
 * )
 */
export function extractData<
	TResponses extends Record<number, any>,
	TSuccessStatus extends keyof TResponses
>(successStatus: TSuccessStatus) {
	return (
		response: AxiosResponse<TResponses[keyof TResponses]>
	): TResponses[TSuccessStatus] | null => {
		if (isStatus<TResponses, TSuccessStatus>(response, successStatus)) {
			return response.data;
		}
		return null;
	};
}

/**
 * RxJS operator that extracts data from hey-api SDK responses in an Observable stream.
 *
 * Transforms a stream of Axios responses into a stream of extracted data,
 * returning the typed data for successful status codes or null for others.
 *
 * @param successStatus - The HTTP status code considered successful (typically 200 or 201)
 * @returns RxJS operator that transforms AxiosResponse to extracted data or null
 *
 * @example
 * return defer(() => getDriverLocationApi({ path: { uuid: legId } })).pipe(
 *   mapExtractData<GetDriverLocationResponses, 200>(200)
 * );
 */
export function mapExtractData<
	TResponses extends Record<number, any>,
	TSuccessStatus extends keyof TResponses
>(
	successStatus: TSuccessStatus
): OperatorFunction<
	AxiosResponse<TResponses[keyof TResponses]>,
	TResponses[TSuccessStatus] | null
> {
	return map(extractData<TResponses, TSuccessStatus>(successStatus));
}

/**
 * RxJS operator that filters responses by status code and extracts data with type narrowing.
 *
 * Unlike `mapExtractData` which returns `T | null`, this operator uses type guards to:
 * 1. Filter the stream to only include responses with the specified status
 * 2. Narrow the type to the exact response type (no null union)
 *
 * Non-matching status codes are filtered out from the stream.
 *
 * @param successStatus - The HTTP status code to filter and extract (typically 200 or 201)
 * @returns RxJS operator that filters and narrows response types
 *
 * @example
 * // filterExtractData returns narrow type (no null!)
 * defer(() => getDriverLocationApi({ path: { uuid } })).pipe(
 *   filterExtractData<GetDriverLocationResponses, 200>(200),
 *   tap((data) => {
 *     data.licensePlate // ✅ Type-safe, no null check needed
 *   })
 * );
 */
export function filterExtractData<
	TResponses extends Record<number, any>,
	TSuccessStatus extends keyof TResponses
>(
	successStatus: TSuccessStatus
): OperatorFunction<
	AxiosResponse<TResponses[keyof TResponses]>,
	TResponses[TSuccessStatus]
> {
	return (source$) =>
		source$.pipe(
			filter(
				(
					response
				): response is AxiosResponse<TResponses[TSuccessStatus]> & {
					status: TSuccessStatus;
				} => isStatus<TResponses, TSuccessStatus>(response, successStatus)
			),
			map((response) => response.data)
		);
}
